<script setup>import { ref as _ref } from 'vue';

  import { nextTick } from "vue";
  import { Flash } from "~/jose_modern/flash.js";
  import { requestJson } from "~/recipal-app/controllers/shared/requests.js";
  import ConfirmDialog from "./shared/confirm-dialog.vue";

  const props = defineProps({
    attachments: { type: Array, default: () => [] },
    basePath: { type: String, required: true },
    editable: { type: Boolean, default: false },
  });

  function attachmentPath(attachment) {
    return `${props.basePath}/${attachment.id}`;
  }

  let list = _ref([...props.attachments]);
  let isDragging = _ref(false);
  let fileInput = _ref(null);
  let container = _ref(null);
  let renamingId = _ref(null);
  let renameValue = _ref("");
  let renameInputWidth = _ref(null);
  let savingId = _ref(null);
  let deletingId = _ref(null);
  let showConfirmDelete = _ref(false);
  let pendingDeleteAttachment = _ref(null);

  function iconFor(contentType) {
    if (!contentType) return "fa-solid fa-file-alt";
    if (contentType.startsWith("image/")) return "fa-solid fa-file-image";
    if (contentType === "application/pdf") return "fa-solid fa-file-pdf";
    if (contentType.includes("word") || contentType.includes("opendocument.text"))
      return "fa-solid fa-file-word";
    if (contentType.includes("excel") || contentType.includes("spreadsheet"))
      return "fa-solid fa-file-excel";
    return "fa-solid fa-file-alt";
  }

  function nameFromFile(file) {
    return file.name.replace(/\.[^.]+$/, "");
  }

  function onDragOver(e) {
    e.preventDefault();
    isDragging.value = true;
  }

  function onDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      isDragging.value = false;
    }
  }

  function onDrop(e) {
    e.preventDefault();
    isDragging.value = false;
    uploadFiles(e.dataTransfer.files);
  }

  function onFileSelected(e) {
    uploadFiles(e.target.files);
    e.target.value = "";
  }

  async function uploadFiles(files) {
    await Promise.all(Array.from(files).map(uploadFile));
  }

  async function uploadFile(file) {
    const tempId = `pending-${Date.now()}-${Math.random()}`;
    list.value.push({ id: tempId, name: nameFromFile(file), content_type: file.type, uploading: true });

    const formData = new FormData();
    formData.append("attachment[name]", nameFromFile(file));
    formData.append("attachment[file]", file);

    try {
      const { response, data } = await requestJson(props.basePath, {
        method: "POST",
        body: formData,
      });

      const idx = list.value.findIndex((a) => a.id === tempId);
      if (response.ok) {
        if (idx !== -1) list.value.splice(idx, 1, data);
        Flash.modelCreated("Attachment", true);
      } else {
        if (idx !== -1) list.value.splice(idx, 1);
        Flash.error(data?.errors?.join(", ") || "Upload failed");
      }
    } catch {
      list.value = list.value.filter((a) => a.id !== tempId);
      Flash.error("Upload failed");
    }
  }

  async function startRename(e, attachment) {
    const nameEl = e.currentTarget.closest(".attachment-row").querySelector(".attachment-name");
    renameInputWidth.value = nameEl ? nameEl.offsetWidth : null;
    renamingId.value = attachment.id;
    renameValue.value = attachment.name;
    await nextTick();
    container.value?.querySelector(".rename-input")?.focus();
  }

  function cancelRename() {
    renamingId.value = null;
    renameValue.value = "";
    renameInputWidth.value = null;
  }

  async function confirmRename(attachment) {
    savingId.value = attachment.id;
    try {
      const { response, data } = await requestJson(attachmentPath(attachment), {
        method: "PATCH",
        body: JSON.stringify({ attachment: { name: renameValue.value } }),
      });

      if (response.ok) {
        attachment.name = data.name;
        cancelRename();
        Flash.modelUpdated("Attachment", true);
      } else {
        Flash.error(data?.errors?.join(", ") || "Rename failed");
      }
    } catch {
      Flash.error("Rename failed");
    } finally {
      savingId.value = null;
    }
  }

  function deleteAttachment(attachment) {
    pendingDeleteAttachment.value = attachment;
    showConfirmDelete.value = true;
  }

  async function confirmDelete() {
    const attachment = pendingDeleteAttachment.value;
    deletingId.value = attachment.id;
    try {
      const { response } = await requestJson(attachmentPath(attachment), { method: "DELETE" });

      if (response.ok) {
        list.value = list.value.filter((a) => a.id !== attachment.id);
        Flash.modelRemoved("Attachment", true);
      } else {
        Flash.error("Could not delete attachment");
      }
    } catch {
      Flash.error("Could not delete attachment");
    } finally {
      deletingId.value = null;
    }
  }
</script>

<template>
  <div ref="container">
    <span class="title is-5 is-block">
      <span class="icon mr-2"><i class="fa-solid fa-paperclip"></i></span>
      Attachments
    </span>

    <div
      v-if="editable"
      class="dropzone"
      :class="{ 'is-dragging': isDragging }"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
      @click="fileInput.click()"
    >
      <input ref="fileInput" type="file" multiple class="is-hidden" @change="onFileSelected" />
      <span class="icon is-medium"><i class="fa-solid fa-cloud-arrow-up fa-lg"></i></span>
      <p class="mt-1 is-hidden-mobile">
        Drag &amp; drop or <span class="has-text-link">click to attach</span>
      </p>
      <p class="mt-1 is-hidden-tablet"><span class="has-text-link">Touch to attach</span></p>
    </div>

    <div v-if="list.length" class="attachment-list mt-3">
      <div v-for="attachment in list" :key="attachment.id" class="attachment-row">
        <span
          class="icon"
          :class="
            attachment.uploading || deletingId === attachment.id
              ? 'has-text-grey-light'
              : 'has-text-grey'
          "
        >
          <i
            :class="
              attachment.uploading || deletingId === attachment.id
                ? 'fa-solid fa-spinner fa-spin'
                : iconFor(attachment.content_type)
            "
          ></i>
        </span>

        <template v-if="attachment.uploading">
          <span class="attachment-name has-text-grey is-italic" :title="attachment.name">
            {{ attachment.name }}
          </span>
          <span class="has-text-grey-light is-size-7">Uploading…</span>
        </template>

        <template v-else-if="deletingId === attachment.id">
          <span class="attachment-name has-text-grey is-italic" :title="attachment.name">{{
            attachment.name
          }}</span>
          <span class="has-text-grey-light is-size-7">Removing…</span>
        </template>

        <template v-else-if="renamingId === attachment.id">
          <input
            v-model="renameValue"
            class="input is-small rename-input"
            :style="renameInputWidth ? { width: renameInputWidth + 'px' } : {}"
            :disabled="savingId === attachment.id"
            @keydown.enter="confirmRename(attachment)"
            @keydown.escape="cancelRename"
          />
          <button
            class="button is-small is-success is-light"
            :class="{ 'is-loading': savingId === attachment.id }"
            :disabled="savingId === attachment.id"
            @click.prevent="confirmRename(attachment)"
          >
            <span class="icon is-small"><i class="fa-solid fa-check"></i></span>
          </button>
          <button
            class="button is-small is-light"
            :disabled="savingId === attachment.id"
            @click.prevent="cancelRename"
          >
            <span class="icon is-small"><i class="fa-solid fa-xmark"></i></span>
          </button>
        </template>

        <template v-else>
          <a class="attachment-name" :href="attachment.url" :title="attachment.name">{{
            attachment.name
          }}</a>
          <template v-if="editable">
            <button
              class="button is-ghost is-small row-action"
              @click.prevent="startRename($event, attachment)"
            >
              <span class="icon is-small"><i class="fa-solid fa-pen"></i></span>
            </button>
            <button
              class="delete is-small row-action"
              @click.prevent="deleteAttachment(attachment)"
            ></button>
          </template>
        </template>
      </div>
    </div>

    <p v-else-if="!editable" class="has-text-grey is-size-7 is-italic mt-2">No attachments</p>

    <ConfirmDialog
      v-model="showConfirmDelete"
      title="Remove Attachment"
      message=""
      confirm-text="Remove"
      :is-loading="deletingId !== null"
      @confirm="confirmDelete"
    >
      <p>
        Remove attached file <b>{{ pendingDeleteAttachment?.name }}</b
        >?
      </p>
    </ConfirmDialog>
  </div>
</template>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  .dropzone {
    border: 2px dashed $grey-lighter;
    border-radius: $radius;
    padding: 1.5rem;
    text-align: center;
    cursor: pointer;
    color: $grey;
    transition:
      border-color 0.15s,
      background-color 0.15s;

    &:hover,
    &.is-dragging {
      border-color: $link;
      background-color: $light;
    }
  }

  .attachment-list {
    display: flex;
    flex-direction: column;
  }

  .attachment-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    border-radius: $radius-small;
    transition: background-color 0.1s;

    .icon {
      flex-shrink: 0;
    }

    &:hover {
      background-color: $white-ter;
    }

    .row-action {
      opacity: 0;
      transition: opacity 0.15s;
    }

    &:hover .row-action {
      opacity: 1;
    }

    .button:hover {
      border: 1px solid $grey-light;
    }

    @include touch {
      .row-action {
        opacity: 1;
      }
    }

    .rename-input {
      flex-grow: 1;
      max-width: unset;
    }
  }

  .attachment-name {
    flex-grow: 1;
    font-size: 0.9rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
